import React, { useEffect, useMemo, useState } from "react";
import { nanoid } from "nanoid";
import { toast } from "react-toastify";
import {
	createCautionSet,
	deleteCautionSet,
	fetchCautionSets,
	fetchCautionSuggestions,
	OrganizerCautionPhrase,
	OrganizerCautionSet,
	OrganizerCautionSetPayload,
	updateCautionSet,
} from "../../../services/cautionService";

type CautionItem = {
	id: string;
	content: string;
};

type EventCautionsProps = {
	items: CautionItem[];
	onChange: (items: CautionItem[]) => void;
};

type CautionSetForm = {
	id?: number;
	name: string;
	description?: string;
	items: { id: string; content: string }[];
};

const createEmptySetForm = (): CautionSetForm => ({
	name: "",
	description: "",
	items: [{ id: nanoid(6), content: "" }],
});

const EventCautions: React.FC<EventCautionsProps> = ({ items, onChange }) => {
	const [suggestions, setSuggestions] = useState<OrganizerCautionPhrase[]>([]);
	const [sets, setSets] = useState<OrganizerCautionSet[]>([]);
	const [isSetModalOpen, setIsSetModalOpen] = useState(false);
	const [activeInputId, setActiveInputId] = useState<string | null>(null);
	const [selectedSetOption, setSelectedSetOption] = useState("");
	const [setForm, setSetForm] = useState<CautionSetForm>(createEmptySetForm());
	const [isSavingSet, setIsSavingSet] = useState(false);
	const [isDeletingSet, setIsDeletingSet] = useState(false);

	useEffect(() => {
		const load = async () => {
			try {
				const [phrases, fetchedSets] = await Promise.all([
					fetchCautionSuggestions(),
					fetchCautionSets(),
				]);
				setSuggestions(phrases);
				setSets(fetchedSets);
			} catch (error) {
				console.error("Failed to load caution metadata", error);
				toast.error("注意事項データの取得に失敗しました");
			}
		};

		load();
	}, []);

	const ensureItemList = (next: CautionItem[]) => {
		if (next.length === 0) {
			return [{ id: nanoid(8), content: "" }];
		}
		return next;
	};

	const handleChange = (id: string, value: string) => {
		onChange(
			items.map((item) => (item.id === id ? { ...item, content: value } : item))
		);
	};

	const addRow = () => {
		onChange([...items, { id: nanoid(8), content: "" }]);
	};

	const removeRow = (id: string) => {
		onChange(ensureItemList(items.filter((item) => item.id !== id)));
	};

	const filteredSuggestions = useMemo(() => {
		const keyword =
			items.find((item) => item.id === activeInputId)?.content || "";
		const normalized = keyword.trim().toLowerCase();
		const base = normalized
			? suggestions.filter((phrase) =>
					phrase.content.toLowerCase().includes(normalized)
			  )
			: suggestions;
		return base.slice(0, 5);
	}, [activeInputId, items, suggestions]);

	const normalizeItemValues = (list: CautionItem[]) =>
		list
			.map((item) => item.content.trim())
			.filter((content) => content.length > 0);

	const mapCurrentItemsToSetForm = () => {
		const normalized = normalizeItemValues(items).map((content) => ({
			id: nanoid(6),
			content,
		}));
		if (normalized.length === 0) {
			return [{ id: nanoid(6), content: "" }];
		}
		return normalized;
	};

	const openQuickSetModal = () => {
		setSetForm({
			id: undefined,
			name: "",
			description: "",
			items: mapCurrentItemsToSetForm(),
		});
		setIsSetModalOpen(true);
	};

	useEffect(() => {
		if (!sets.length) {
			setSelectedSetOption("");
			return;
		}

		const current = normalizeItemValues(items);

		const match = sets.find((set) => {
			const normalizedSetItems = set.items
				.slice()
				.sort((a, b) => a.position - b.position)
				.map((item) => item.content.trim())
				.filter((content) => content.length > 0);

			if (normalizedSetItems.length !== current.length) {
				return false;
			}

			return normalizedSetItems.every(
				(content, index) => content === current[index]
			);
		});

		setSelectedSetOption(match ? String(match.id) : "");
	}, [items, sets]);

	const closeSetModal = () => {
		setIsSetModalOpen(false);
		setSetForm(createEmptySetForm());
	};

	const handleSelectExistingSet = (set: OrganizerCautionSet) => {
		setSetForm({
			id: set.id,
			name: set.name,
			description: set.description ?? "",
			items:
				set.items.length > 0
					? set.items
							.sort((a, b) => a.position - b.position)
							.map((item) => ({
								id: nanoid(6),
								content: item.content,
							}))
					: [{ id: nanoid(6), content: "" }],
		});
	};

	const normalizeSetPayload = (): OrganizerCautionSetPayload | null => {
		const name = setForm.name.trim();
		const itemsPayload = setForm.items
			.map((item) => item.content.trim())
			.filter((content) => content.length > 0)
			.map((content) => ({ content }));

		if (!name) {
			toast.error("セット名を入力してください");
			return null;
		}

		if (itemsPayload.length === 0) {
			toast.error("セット内容を1件以上入力してください");
			return null;
		}

		return {
			name,
			description: setForm.description?.trim() || null,
			items: itemsPayload,
		};
	};

	const handleSaveSet = async () => {
		const payload = normalizeSetPayload();
		if (!payload) return;

		try {
			setIsSavingSet(true);
			let savedSet: OrganizerCautionSet;
			if (setForm.id) {
				savedSet = await updateCautionSet(setForm.id, payload);
			} else {
				savedSet = await createCautionSet(payload);
			}
			setSets((prev) => {
				const exists = prev.some((item) => item.id === savedSet.id);
				if (exists) {
					return prev.map((item) =>
						item.id === savedSet.id ? savedSet : item
					);
				}
				return [savedSet, ...prev];
			});
			handleSelectExistingSet(savedSet);
			toast.success("注意事項セットを保存しました");
		} catch (error) {
			console.error("Failed to save caution set", error);
			toast.error("注意事項セットの保存に失敗しました");
		} finally {
			setIsSavingSet(false);
		}
	};

	const handleDeleteSet = async () => {
		if (!setForm.id) return;
		if (!window.confirm("このセットを削除しますか？")) {
			return;
		}
		try {
			setIsDeletingSet(true);
			await deleteCautionSet(setForm.id);
			setSets((prev) => prev.filter((set) => set.id !== setForm.id));
			setSetForm(createEmptySetForm());
			toast.info("セットを削除しました");
		} catch (error) {
			console.error("Failed to delete caution set", error);
			toast.error("セットの削除に失敗しました");
		} finally {
			setIsDeletingSet(false);
		}
	};

	const applySetToItems = (value: string) => {
		if (!value) return;
		const target = sets.find((set) => String(set.id) === value);
		if (!target) return;

		const existingTexts = items.map((item) => item.content.trim());
		const additions = target.items
			.slice()
			.sort((a, b) => a.position - b.position)
			.map((item) => item.content)
			.filter((content) => content.trim().length > 0)
			.filter((content) => !existingTexts.includes(content.trim()))
			.map((content) => ({ id: nanoid(8), content }));

		if (additions.length === 0) {
			toast.info("すべての注意事項は既に追加済みです");
		} else {
			onChange([...items, ...additions]);
			toast.success(`${target.name} を適用しました`);
		}
		setSelectedSetOption(String(target.id));
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
			<div className="flex flex-col gap-2">
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
					注意事項
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					イベント参加者に伝えておきたい注意事項を箇条書きで入力してください。過去に登録した内容は入力補完から選べます。
				</p>
			</div>

			{sets.length > 0 && (
				<div className="flex flex-col md:flex-row gap-4 items-start">
					<div className="w-full md:flex-1">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							セットを適用
						</label>
						<select
							value={selectedSetOption}
							onChange={(e) => {
								setSelectedSetOption(e.target.value);
								if (e.target.value) {
									applySetToItems(e.target.value);
								}
							}}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
						>
							<option value="">セットを選択してください</option>
							{sets.map((set) => (
								<option key={set.id} value={set.id}>
									{set.name}
								</option>
							))}
						</select>
					</div>
				</div>
			)}

			<div className="space-y-3">
				{items.map((item) => {
					const trimmed = item.content.trim();
					const showSuggestions =
						activeInputId === item.id &&
						filteredSuggestions.length > 0 &&
						(trimmed.length > 0 || suggestions.length > 0);

					return (
						<div key={item.id} className="flex items-start gap-3 relative">
							<div className="pt-3 text-gray-400 dark:text-gray-500">・</div>
							<div className="flex-1">
								<div className="relative">
									<input
										type="text"
										value={item.content}
										onChange={(e) => handleChange(item.id, e.target.value)}
										onFocus={() => setActiveInputId(item.id)}
										onBlur={() => {
											setTimeout(() => setActiveInputId(null), 120);
										}}
										placeholder="例: 再入場不可 / 20歳未満の方は入場不可 など"
										className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition"
									/>
									{showSuggestions && (
										<div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
											{filteredSuggestions.map((suggestion) => (
												<button
													type="button"
													key={suggestion.id}
													className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-violet-50 dark:hover:bg-violet-900/40 transition-colors"
													onMouseDown={(e) => {
														e.preventDefault();
														handleChange(item.id, suggestion.content);
														setActiveInputId(null);
													}}
												>
													{suggestion.content}
												</button>
											))}
										</div>
									)}
								</div>
							</div>
							<button
								type="button"
								onClick={() => removeRow(item.id)}
								className="mt-2 text-gray-400 hover:text-red-500 transition-colors"
								aria-label="注意事項を削除"
							>
								<svg
									className="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>
					);
				})}
			</div>

			<div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
				<button
					type="button"
					onClick={addRow}
					className="flex-1 inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-dashed border-violet-300 dark:border-violet-500 text-violet-600 dark:text-violet-300 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/30 transition"
				>
					<svg
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 4v16m8-8H4"
						/>
					</svg>
					注意事項を追加
				</button>
				<button
					type="button"
					onClick={openQuickSetModal}
					className="flex-1 inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
				>
					<svg
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M5 13l4 4L19 7"
						/>
					</svg>
					セットとして登録
				</button>
			</div>

			{isSetModalOpen && (
				<div className="fixed inset-0 z-40 flex items-center justify-center">
					<div
						className="absolute inset-0 bg-black/50"
						onClick={closeSetModal}
					/>
					<div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
						<div className="p-6 space-y-6">
							<div className="flex items-start justify-between">
								<div>
									<h4 className="text-lg font-semibold text-gray-900 dark:text-white">
										注意事項セットを管理
									</h4>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										よく使う注意事項のセットを登録して、イベント作成を効率化しましょう。
									</p>
								</div>
								<button
									type="button"
									onClick={closeSetModal}
									className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-100"
								>
									<svg
										className="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>

							<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
								<div className="lg:col-span-2 space-y-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											セット名
										</label>
										<input
											type="text"
											value={setForm.name}
											onChange={(e) =>
												setSetForm((prev) => ({
													...prev,
													name: e.target.value,
												}))
											}
											className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											説明（任意）
										</label>
										<textarea
											value={setForm.description}
											onChange={(e) =>
												setSetForm((prev) => ({
													...prev,
													description: e.target.value,
												}))
											}
											className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px]"
										/>
									</div>

									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<p className="text-sm font-medium text-gray-700 dark:text-gray-300">
												セット内容
											</p>
											<button
												type="button"
												onClick={() =>
													setSetForm((prev) => ({
														...prev,
														items: [
															...prev.items,
															{ id: nanoid(6), content: "" },
														],
													}))
												}
												className="text-sm text-violet-600 dark:text-violet-400 hover:underline"
											>
												注意事項を追加
											</button>
										</div>

										{setForm.items.map((item, index) => (
											<div key={item.id} className="flex items-start gap-2">
												<span className="pt-2 text-gray-400 dark:text-gray-500 text-sm">
													{index + 1}.
												</span>
												<div className="flex-1">
													<input
														type="text"
														value={item.content}
														onChange={(e) =>
															setSetForm((prev) => ({
																...prev,
																items: prev.items.map((row) =>
																	row.id === item.id
																		? { ...row, content: e.target.value }
																		: row
																),
															}))
														}
														className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
														placeholder="注意事項を入力"
													/>
												</div>
												<button
													type="button"
													onClick={() =>
														setSetForm((prev) => ({
															...prev,
															items:
																prev.items.length > 1
																	? prev.items.filter(
																			(row) => row.id !== item.id
																	  )
																	: [{ id: nanoid(6), content: "" }],
														}))
													}
													className="mt-2 text-gray-400 hover:text-red-500"
													aria-label="セット項目を削除"
												>
													<svg
														className="w-4 h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M6 18L18 6M6 6l12 12"
														/>
													</svg>
												</button>
											</div>
										))}
									</div>

									<div className="flex items-center justify-end gap-3 pt-4">
										{setForm.id && (
											<button
												type="button"
												onClick={handleDeleteSet}
												disabled={isDeletingSet}
												className="px-3 sm:px-4 py-2 border border-transparent rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
											>
												{isDeletingSet ? "削除中..." : "削除"}
											</button>
										)}
										<button
											type="button"
											onClick={handleSaveSet}
											disabled={isSavingSet}
											className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
										>
											{isSavingSet ? "保存中..." : "セットを保存"}
										</button>
									</div>
								</div>

								<div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 max-h-[420px] overflow-y-auto">
									<div className="flex items-center justify-between mb-3">
										<h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
											登録済みセット
										</h5>
										<button
											type="button"
											onClick={() => setSetForm(createEmptySetForm())}
											className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
										>
											新規作成
										</button>
									</div>

									{sets.length === 0 ? (
										<p className="text-sm text-gray-500 dark:text-gray-400">
											まだセットは登録されていません。
										</p>
									) : (
										<ul className="space-y-2">
											{sets.map((set) => (
												<li key={set.id}>
													<button
														type="button"
														onClick={() => handleSelectExistingSet(set)}
														className={`w-full text-left px-3 py-2 rounded-lg border transition ${
															setForm.id === set.id
																? "border-violet-400 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200"
																: "border-transparent hover:border-gray-200 dark:hover:border-gray-600 text-gray-700 dark:text-gray-200"
														}`}
													>
														<div className="font-medium">{set.name}</div>
														{set.description && (
															<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
																{set.description}
															</p>
														)}
													</button>
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export type { CautionItem };
export default EventCautions;
